import React, { useState, useCallback, useEffect } from "react";

import { Plus } from "icons";

import domainsApi from "apis/domains";
import {
  PageLoader,
  Button,
  SubHeader,
  Typography,
} from "clearsense-ui/components";
import ContentBox from "components/Common/ContentBox";
import FilterHeader from "components/Common/FilterHeader";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import QuickFilter from "components/Common/QuickFilter";
import SummaryBarChart from "components/Common/SummaryBarChart";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";

import DeleteDomains from "./DeleteDomains";
import DomainsTable from "./DomainsTable";
import NewDomain from "./NewDomain";

import { COLUMNS, COLUMN_DATA } from "../constants/domainsTable";

const Domains = ({ userPermissions = [] }) => {
  const date = new Date();
  const [isCreateDomainOpen, setIsCreateDomainOpen] = useState(false);
  const [domainsData, setDomainsData] = useState([]);
  const [domainsCount, setDomainsCount] = useState(0);
  const [checkedRows, setCheckedRows] = useState([]);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [editedRecord, setEditedRecord] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterConditions, setFilterConditions] = useState([]);
  const [rowToBeDeleted, setRowToBeDeleted] = useState({});
  const [summaryData, setSummaryData] = useState([]);
  const [isGraphLoading, setIsGraphLoading] = useState(true);
  const [sort, setSort] = useState("top");
  const [filter, setFilter] = useState({
    filterBy: "",
    filterValue: "",
  });
  const quickFilterData = [
    {
      label: "Missing Descriptions",
      value: domainsData?.missing_description_count,
      onClick: () =>
        setFilter({
          filterBy: "description =",
          filterValue: "",
        }),
    },
    {
      label: "CDE added in last 30 days",
      value: domainsData?.created_in_last_30_days_count,
      onClick: () =>
        setFilter({
          filterBy: "created_at>",
          filterValue: new Date(
            date.setDate(date.getDate() - 30)
          ).toUTCString(),
        }),
    },
  ];

  const handleEdit = record => {
    setIsEdit(true);
    setIsCreateDomainOpen(true);
    setEditedRecord(record);
  };

  const handleDelete = record => {
    setRowToBeDeleted(record);
    setIsDeleteAlertOpen(true);
  };

  const fetchDomainSummary = async () => {
    try {
      setIsGraphLoading(true);
      const { data } = await domainsApi.fetchDomainSummary(sort);
      setSummaryData(data?.summary_data);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsGraphLoading(false);
    }
  };

  const fetchDomainsData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const {
        data: { domains },
      } = await domainsApi.fetch(
        search.toLowerCase(),
        currentPage,
        pageSize,
        filter.filterBy,
        filter.filterValue
      );
      setDomainsData(domains);
      setDomainsCount(domains.domains_count);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  const filterParams = useSearchAndPaginate(fetchDomainsData, {
    page: () => setCheckedRows([]),
    pageSize: () => setCheckedRows([]),
  });

  const columnHeaderSelector = useColumnHeaderSelector(
    COLUMN_DATA(handleEdit, handleDelete, userPermissions)
  );

  useEffect(() => {
    fetchDomainSummary();
  }, [sort]);

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <SubHeader
        className="mt-8 mb-2"
        breadcrumbs={[
          { label: "Domains", link: "#" },
          { label: "Overview", link: "#" },
        ]}
        actionBlock={
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="create"
          >
            <Button
              label="Create Domain"
              icon={Plus}
              onClick={() => {
                setIsEdit(false);
                setIsCreateDomainOpen(true);
              }}
              data-cy="new-domain-button"
            />
          </PermissibleWrapper>
        }
      />
      <ContentBox className="flex flex-col justify-center space-y-4 mt-4 items-center bg-white dark:bg-[#2f3941] w-full dark:text-white">
        <Typography style="h3" type="semibold" className="justify-center flex">
          Summary
        </Typography>
        <div className="flex space-x-4 w-full">
          <QuickFilter
            className="w-1/2"
            quickFilterData={quickFilterData}
            disabled={isLoading}
          />
          <SummaryBarChart
            topFiveAction={() => setSort("top")}
            bottomFiveAction={() => setSort("bottom")}
            className="w-1/2"
            data={summaryData}
            isLoading={isGraphLoading}
            title="Total Business Terms by Domain"
          />
        </div>
      </ContentBox>
      <FilterHeader
        entity="Domain"
        columnHeaderSelector={columnHeaderSelector}
        filterParams={filterParams}
        count={domainsCount}
        filterConditions={filterConditions}
        setFilterConditions={setFilterConditions}
        checkedRows={checkedRows}
        setIsDeleteAlertOpen={setIsDeleteAlertOpen}
        userPermissions={userPermissions}
        columnOptions={COLUMNS}
      />
      {isLoading ? (
        <PageLoader />
      ) : (
        <DomainsTable
          filterParams={filterParams}
          domainsCount={domainsCount}
          userPermissions={userPermissions}
          setIsEdit={setIsEdit}
          setIsCreateDomainOpen={setIsCreateDomainOpen}
          columns={columnHeaderSelector.columns}
          domainsData={domainsData}
          checkedRows={checkedRows}
          setCheckedRows={setCheckedRows}
        />
      )}
      <NewDomain
        isCreateDomainOpen={isCreateDomainOpen}
        setIsCreateDomainOpen={setIsCreateDomainOpen}
        fetchDomainsData={() =>
          fetchDomainsData(filterParams.search, 1, filterParams.pageSize)
        }
        editedRecord={editedRecord}
        isEdit={isEdit}
      />
      <DeleteDomains
        checkedRows={checkedRows}
        isDeleteAlertOpen={isDeleteAlertOpen}
        setIsDeleteAlertOpen={setIsDeleteAlertOpen}
        fetchDomainsData={fetchDomainsData}
        setCheckedRows={setCheckedRows}
        filterParams={filterParams}
        setIsLoading={setIsLoading}
        rowToBeDeleted={rowToBeDeleted}
        setRowToBeDeleted={setRowToBeDeleted}
      />
    </div>
  );
};

export default Domains;
